import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'; // Import FBXLoader

const MOVEMENT_SPEED = 0.2; // User specified 0.2
// const ROTATION_SPEED = 0.03;

const isMobile = /Mobi|Android/i.test(navigator.userAgent);
const ROTATION_SPEED = isMobile ? 0.12 : 0.03;
const ANIMATION_SPEED_MULTIPLIER = 2.0;

// Define room boundaries for collision detection
const ROOM_SIZE = 75; // Defines the half-width/half-depth of the square room
const MIN_X = -ROOM_SIZE;
const MAX_X = ROOM_SIZE;
const MIN_Z = -ROOM_SIZE;
const MAX_Z = ROOM_SIZE;
const WALL_HEIGHT = 15;
const CHARACTER_SIZE = 0.01; // Reverted to your original value
const CAMERA_ROTATION_SPEED = isMobile ? 0.15 : 0.02;

// Jump constants (scaled for the new CHARACTER_SIZE)
const JUMP_INITIAL_VELOCITY = 1.5; // Reverted to your original value
const GRAVITY = 0.09; // Reverted to your original value

function App() {
  const mountRef = useRef(null); // Reference to the DOM element for the Three.js canvas
  const [isSceneInitialized, setIsSceneInitialized] = useState(false); // State to track scene initialization
  const characterRef = useRef(null); // Reference to the 3D character object (FBX model)
  const cameraRef = useRef(null); // Reference to the Three.js camera
  const mixerRef = useRef(null); // Reference for AnimationMixer
  const clockRef = useRef(new THREE.Clock()); // Clock for animation timing
  const loader = useRef(new FBXLoader()); // FBXLoader instance
  
  // Ref for walls
  const wallsRef = useRef([]);

  // Camera rotation state
  const cameraYaw = useRef(0); // Yaw (horizontal rotation)
  const cameraPitch = useRef(0); // Pitch (vertical rotation)
  

  // Movement and Jump states
  const keysPressed = useRef({});
  const isJumping = useRef(false);
  const jumpVelocity = useRef(0);
  const characterBaseY = useRef(0); // Stores the character's initial Y position for landing

  // Touch state for mobile controls
  const touchStartPos = useRef({ x: 0, y: 0 }); // Stores the initial touch position for delta calculation
  const touchMoveIdentifier = useRef(null); // To track a specific touch for character movement
  const touchCameraIdentifier = useRef(null); // To track a specific touch for camera control
  const isTouchingMove = useRef(false); // Flag to indicate if a movement touch is active
  const isTouchingCamera = useRef(false); // Flag to indicate if a camera touch is active
  // joystickCenter and joystickActive are not directly used in the current touch logic, but kept for consistency
  const joystickCenter = useRef({ x: 0, y: 0 });
  const joystickActive = useRef(false);

  // Refs for animation actions for consistent access
  const runningActionRef = useRef(null);
  const jumpActionRef = useRef(null);

  useEffect(() => {
    let initTimeout;

    if (!mountRef.current) {
      console.error('mountRef.current is null, cannot initialize Three.js');
      return;
    }

    let scene, camera, renderer;

    // --- Keyboard Controls ---
    const handleKeyDown = (event) => {
      keysPressed.current[event.code] = true;
      if (event.code === 'Space' && !isJumping.current) {
        isJumping.current = true;
        jumpVelocity.current = JUMP_INITIAL_VELOCITY;
        if (jumpActionRef.current && runningActionRef.current) {
          runningActionRef.current.stop(); // Stop running animation
          jumpActionRef.current.reset().play(); // Play jump animation
        }
      }
    };

    const handleKeyUp = (event) => {
      keysPressed.current[event.code] = false;
    };

    // --- Touch Controls for Mobile (IMPROVED) ---
    const handleTouchStart = (event) => {
      event.preventDefault(); // Prevent scrolling and zooming

      for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        if (touch.clientX < window.innerWidth / 2 && touchMoveIdentifier.current === null) {
          // Left half for character movement
          touchMoveIdentifier.current = touch.identifier;
          touchStartPos.current.x = touch.clientX; // Store initial X for delta
          touchStartPos.current.y = touch.clientY; // Store initial Y for delta
          isTouchingMove.current = true;
        } else if (touch.clientX >= window.innerWidth / 2 && touchCameraIdentifier.current === null) {
          // Right half for camera control
          touchCameraIdentifier.current = touch.identifier;
          touchStartPos.current.x = touch.clientX; // Store initial X for delta
          touchStartPos.current.y = touch.clientY; // Store initial Y for delta
          isTouchingCamera.current = true;
        }
      }
    };

    const handleTouchMove = (event) => {
      event.preventDefault(); // Prevent scrolling

      for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];

        if (touch.identifier === touchMoveIdentifier.current) {
          // Character Movement (Left Half)
          const deltaX = touch.clientX - touchStartPos.current.x;
          const deltaY = touch.clientY - touchStartPos.current.y; // Vertical movement for forward/backward

          if (characterRef.current) {
            // Character rotation based on horizontal drag
            const rotationDelta = -deltaX * ROTATION_SPEED * 0.05; // Adjust sensitivity
            characterRef.current.rotation.y += rotationDelta;

            // Character forward/backward movement based on vertical drag
            const moveMagnitude = -deltaY * MOVEMENT_SPEED * 0.05; // Adjust sensitivity and direction
            characterRef.current.translateZ(moveMagnitude);

            // Trigger running animation if significant movement is detected
            keysPressed.current['ArrowUp'] = Math.abs(moveMagnitude) > 0.001 || Math.abs(rotationDelta) > 0.001;
          }
          // Update touchStartPos for continuous delta calculation from current position
          touchStartPos.current.x = touch.clientX;
          touchStartPos.current.y = touch.clientY;

        } else if (touch.identifier === touchCameraIdentifier.current) {
          // Camera Rotation (Right Half)
          const deltaX = touch.clientX - touchStartPos.current.x;
          const deltaY = touch.clientY - touchStartPos.current.y;

          cameraYaw.current -= deltaX * CAMERA_ROTATION_SPEED * 0.02; // Reduced sensitivity
          cameraPitch.current -= deltaY * CAMERA_ROTATION_SPEED * 0.02; // Reduced sensitivity

          cameraPitch.current = Math.min(cameraPitch.current, Math.PI / 2 - 0.1);
          cameraPitch.current = Math.max(cameraPitch.current, -Math.PI / 2 + 0.1);

          // Update touchStartPos for continuous delta calculation from current position
          touchStartPos.current.x = touch.clientX;
          touchStartPos.current.y = touch.clientY;
        }
      }
    };

    const handleTouchEnd = (event) => {
      event.preventDefault(); // Prevent scrolling

      for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        if (touch.identifier === touchMoveIdentifier.current) {
          touchMoveIdentifier.current = null;
          isTouchingMove.current = false;
          keysPressed.current['ArrowUp'] = false; // Stop character animation
        } else if (touch.identifier === touchCameraIdentifier.current) {
          touchCameraIdentifier.current = null;
          isTouchingCamera.current = false;
        }
      }
    };

    // --- Handle Window Resizing ---
    const onWindowResize = () => {
      if (mountRef.current && camera && renderer) {
        camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
      }
    };

    try {
      // --- Scene Setup ---
      scene = new THREE.Scene();

      // --- Camera Setup ---
      camera = new THREE.PerspectiveCamera(
        75,
        mountRef.current.clientWidth / mountRef.current.clientHeight,
        0.0001,
        1000
      );
      cameraRef.current = camera;
      camera.position.set(0, 5, 10);
      camera.lookAt(0, 0, 0);

      // --- Renderer Setup ---
      renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      mountRef.current.appendChild(renderer.domElement);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      // --- Lighting ---
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
      directionalLight.position.set(5, 10, 5);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 1024;
      directionalLight.shadow.mapSize.height = 1024;
      directionalLight.shadow.camera.near = 0.5;
      directionalLight.shadow.camera.far = 50;
      directionalLight.shadow.camera.left = -ROOM_SIZE * 1.5;
      directionalLight.shadow.camera.right = ROOM_SIZE * 1.5;
      directionalLight.shadow.camera.top = ROOM_SIZE * 1.5;
      directionalLight.shadow.camera.bottom = -ROOM_SIZE * 1.5;
      scene.add(directionalLight);

      // --- Wall Texture Loader & Material ---
      const textureLoader = new THREE.TextureLoader();
      
      const wallMaterial = new THREE.MeshStandardMaterial({
        color: 0x888888, // Default color if texture fails to load
        side: THREE.FrontSide,
      });

      const wallTextureLoadPromise = new Promise((resolve, reject) => {
        textureLoader.load(
          `${window.location.origin}/textures/wall_04.jpg`,
          (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.set(2, 1);
            wallMaterial.map = texture;
            wallMaterial.needsUpdate = true;
            console.log(`Wall texture loaded. Dimensions: ${texture.image.width}x${texture.image.height}, Repeat: ${texture.repeat.x}, ${texture.repeat.y}`);
            resolve();
          },
          undefined,
          (error) => {
            console.warn("Failed to load wall texture, using default color:", error instanceof Error ? error.message : error);
            reject(new Error(`Wall texture load failed: ${error instanceof Error ? error.message : error}`));
          }
        );
      });

      // --- Floor Texture Loader & Material ---
      const planeMaterial = new THREE.MeshStandardMaterial({
        color: 0x666666, // Default color if texture fails to load
        side: THREE.DoubleSide,
      });

      const floorTextureLoadPromise = new Promise((resolve, reject) => {
        textureLoader.load(
          `${window.location.origin}/textures/grass_02.jpg`,
          (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.set(10, 10); // Adjust tiling for floor
            planeMaterial.map = texture;
            planeMaterial.needsUpdate = true;
            console.log(`Floor texture loaded. Dimensions: ${texture.image.width}x${texture.image.height}, Repeat: ${texture.repeat.x}, ${texture.repeat.y}`);
            resolve();
          },
          undefined,
          (error) => {
            console.warn("Failed to load floor texture, using default color:", error instanceof Error ? error.message : error);
            reject(new Error(`Floor texture load failed: ${error instanceof Error ? error.message : error}`));
          }
        );
      });

      // --- Ground Plane ---
      const planeGeometry = new THREE.PlaneGeometry(ROOM_SIZE * 2, ROOM_SIZE * 2);
      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      plane.rotation.x = -Math.PI / 2;
      plane.receiveShadow = true;
      scene.add(plane);


      // --- Room Walls ---
      const wallThickness = 0.1;

      const wall1 = new THREE.Mesh(new THREE.BoxGeometry(ROOM_SIZE * 2 + wallThickness, WALL_HEIGHT, wallThickness), wallMaterial);
      wall1.position.set(0, WALL_HEIGHT / 2, -ROOM_SIZE);
      wall1.castShadow = true;
      wall1.receiveShadow = true;
      scene.add(wall1);
      wallsRef.current.push(wall1);

      const wall2 = new THREE.Mesh(new THREE.BoxGeometry(ROOM_SIZE * 2 + wallThickness, WALL_HEIGHT, wallThickness), wallMaterial);
      wall2.position.set(0, WALL_HEIGHT / 2, ROOM_SIZE);
      wall2.castShadow = true;
      wall2.receiveShadow = true;
      scene.add(wall2);
      wallsRef.current.push(wall2);

      const wall3 = new THREE.Mesh(new THREE.BoxGeometry(wallThickness, WALL_HEIGHT, ROOM_SIZE * 2 - wallThickness), wallMaterial);
      wall3.position.set(-ROOM_SIZE, WALL_HEIGHT / 2, 0);
      wall3.castShadow = true;
      wall3.receiveShadow = true;
      scene.add(wall3);
      wallsRef.current.push(wall3);

      const wall4 = new THREE.Mesh(new THREE.BoxGeometry(wallThickness, WALL_HEIGHT, ROOM_SIZE * 2 - wallThickness), wallMaterial);
      wall4.position.set(ROOM_SIZE, WALL_HEIGHT / 2, 0);
      wall4.castShadow = true;
      wall4.receiveShadow = true;
      scene.add(wall4);
      wallsRef.current.push(wall4);

      // --- Skybox Setup ---
      const cubeTextureLoader = new THREE.CubeTextureLoader();
      const skyboxUrls = [
        `${window.location.origin}/skybox_jpg/px.jpg`,
        `${window.location.origin}/skybox_jpg/nx.jpg`,
        `${window.location.origin}/skybox_jpg/py.jpg`,
        `${window.location.origin}/skybox_jpg/ny.jpg`,
        `${window.location.origin}/skybox_jpg/pz.jpg`,
        `${window.location.origin}/skybox_jpg/nz.jpg`,
      ];
      const skyboxLoadPromise = new Promise((resolve, reject) => {
        cubeTextureLoader.load(
          skyboxUrls,
          (cubeTexture) => {
            scene.background = cubeTexture;
            resolve();
          },
          undefined,
          (error) => {
            console.warn("Failed to load skybox textures, using default background:", error instanceof Error ? error.message : error);
            scene.background = new THREE.Color(0x87CEEB);
            reject(new Error(`Skybox texture load failed: ${error instanceof Error ? error.message : error}`));
          }
        );
      });

      // --- Load FBX Character Model ---
      const fbxLoadPromise = new Promise((resolve, reject) => {
        loader.current.load(
          `${window.location.origin}/models/cube_man.fbx`,
          (fbx) => {
            fbx.scale.set(CHARACTER_SIZE, CHARACTER_SIZE, CHARACTER_SIZE);
            fbx.position.y = 0;
            characterBaseY.current = fbx.position.y;

            fbx.traverse((child) => {
              if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;

                if (child.material) {
                  if (Array.isArray(child.material)) {
                    child.material.forEach(mat => {
                      mat.visible = true;
                      mat.needsUpdate = true;
                      mat.transparent = false;
                      mat.opacity = 1.0;
                      mat.side = THREE.FrontSide;

                      if (mat.type === 'MeshBasicMaterial' || !mat.isMeshStandardMaterial) {
                        console.warn(`Mesh "${child.name}" sub-material uses ${mat.type}. Converting to MeshStandardMaterial.`);
                        const oldColor = (mat.color && mat.color.isColor) ? mat.color.getHex() : 0xaaaaaa;
                        const oldMap = mat.map;
                        mat.dispose();
                        child.material[child.material.indexOf(mat)] = new THREE.MeshStandardMaterial({
                            color: oldColor,
                            map: oldMap,
                            roughness: 0.8,
                            metalness: 0.1,
                            visible: true,
                            transparent: false,
                            opacity: 1.0,
                            side: THREE.FrontSide
                        });
                      }
                      console.log(`Mesh: ${child.name}, Sub-Material Type: ${mat.type}, Visible: ${mat.visible}, Opacity: ${mat.opacity}`);
                    });
                  } else {
                    const mat = child.material;
                    mat.visible = true;
                    mat.needsUpdate = true;
                    mat.transparent = false;
                    mat.opacity = 1.0;
                    mat.side = THREE.FrontSide;

                    if (mat.type === 'MeshBasicMaterial' || !mat.isMeshStandardMaterial) {
                      console.warn(`Mesh "${child.name}" uses ${mat.type}. Converting to MeshStandardMaterial.`);
                      const oldColor = (mat.color && mat.color.isColor) ? mat.color.getHex() : 0xaaaaaa;
                      const oldMap = mat.map;
                      mat.dispose();
                      child.material = new THREE.MeshStandardMaterial({
                          color: oldColor,
                          map: oldMap,
                          roughness: 0.8,
                          metalness: 0.1,
                          visible: true,
                          transparent: false,
                          opacity: 1.0,
                          side: THREE.FrontSide
                      });
                    }
                    console.log(`Mesh: ${child.name}, Material Type: ${mat.type}, Visible: ${mat.visible}, Opacity: ${mat.opacity}`);
                  }
                } else {
                    console.warn(`Mesh "${child.name}" has no material. Assigning a default MeshStandardMaterial.`);
                    child.material = new THREE.MeshStandardMaterial({ color: 0xaaaaaa, visible: true, transparent: false, opacity: 1.0, side: THREE.FrontSide });
                }
              }
            });

            scene.add(fbx);
            characterRef.current = fbx;
            
            // Set up Animation Mixer
            mixerRef.current = new THREE.AnimationMixer(fbx);
            mixerRef.current.timeScale = ANIMATION_SPEED_MULTIPLIER;

            if (fbx.animations && fbx.animations.length > 0) {
              const runClip = fbx.animations.find(clip =>
                clip.name.toLowerCase().includes('run') || clip.name.toLowerCase().includes('running')
              );
              const jumpClip = fbx.animations.find(clip =>
                clip.name.toLowerCase().includes('jump')
              );

              if (runClip) {
                runningActionRef.current = mixerRef.current.clipAction(runClip);
                console.log(`Using animation: ${runClip.name} for running.`);
              } else {
                runningActionRef.current = mixerRef.current.clipAction(fbx.animations[0]);
                console.warn(`No 'run' or 'running' animation found. Defaulting to first animation: ${fbx.animations[0].name}`);
              }

              if (jumpClip) {
                jumpActionRef.current = mixerRef.current.clipAction(jumpClip);
                jumpActionRef.current.setLoop(THREE.LoopOnce);
                jumpActionRef.current.clampWhenFinished = true;
                console.log(`Using animation: ${jumpClip.name} for jumping.`);
              } else {
                console.warn("No 'jump' animation found. Character will only translate vertically.");
              }

              if (runningActionRef.current) {
                runningActionRef.current.play();
                runningActionRef.current.setEffectiveWeight(0);
                runningActionRef.current.enabled = true;
              }
            } else {
              console.warn("No animations found in the FBX model.");
            }

            resolve();
          },
          (xhr) => {
            console.log((xhr.loaded / xhr.total) * 100 + '% loaded (FBX)');
          },
          (error) => {
            console.error('An error occurred while loading the FBX model:', error instanceof Error ? error.message : error);
            reject(new Error(`FBX model load failed: ${error instanceof Error ? error.message : error}`));
          }
        );
      });
      
      Promise.all([fbxLoadPromise, wallTextureLoadPromise, skyboxLoadPromise, floorTextureLoadPromise])
        .then(() => {
          setIsSceneInitialized(true);
          console.log("All essential assets loaded successfully.");
        })
        .catch((error) => {
          console.error("One or more assets failed to load. Scene might not be fully functional:", error);
          setIsSceneInitialized(true);
        });

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      window.addEventListener('resize', onWindowResize);
      mountRef.current.addEventListener('touchstart', handleTouchStart, { passive: false });
      mountRef.current.addEventListener('touchmove', handleTouchMove, { passive: false });
      mountRef.current.addEventListener('touchend', handleTouchEnd);

      initTimeout = setTimeout(() => {
        if (!isSceneInitialized) {
          console.warn("Scene initialization timed out. Displaying scene anyway, check FBX/texture loading errors.");
          setIsSceneInitialized(true);
        }
      }, 15000);

      // --- Animation Loop ---
      const animate = () => {
        requestAnimationFrame(animate);

        const currentCharacter = characterRef.current;
        const currentCamera = cameraRef.current;
        const delta = clockRef.current.getDelta();

        if (mixerRef.current) {
          mixerRef.current.update(delta);
        }

        if (currentCharacter && currentCamera) {
          let moving = false;

          const prevPosition = currentCharacter.position.clone();

          // --- Character Movement Logic (Keyboard) ---
          if (keysPressed.current['ArrowUp']) {
            currentCharacter.translateZ(MOVEMENT_SPEED);
            moving = true;
          }
          if (keysPressed.current['ArrowDown']) {
            currentCharacter.translateZ(-MOVEMENT_SPEED);
            moving = true;
          }
          if (keysPressed.current['ArrowLeft']) {
            currentCharacter.rotation.y += ROTATION_SPEED;
          }
          if (keysPressed.current['ArrowRight']) {
            currentCharacter.rotation.y -= ROTATION_SPEED;
          }

          // --- Jump Physics and Animation Transition on Landing ---
          if (isJumping.current) {
              currentCharacter.position.y += jumpVelocity.current;
              jumpVelocity.current -= GRAVITY;
          
              // Landing detection
              if (currentCharacter.position.y <= characterBaseY.current) {
                  currentCharacter.position.y = characterBaseY.current;
                  isJumping.current = false;
                  jumpVelocity.current = 0;
          
                  if (mixerRef.current) {
                      if (jumpActionRef.current) {
                          jumpActionRef.current.fadeOut(0.2); // Fade out the jump animation
                      }
          
                      const isMovingAfterLand = keysPressed.current['ArrowUp'] || 
                                                keysPressed.current['ArrowDown'] || 
                                                keysPressed.current['ArrowLeft'] || 
                                                keysPressed.current['ArrowRight'] ||
                                                isTouchingMove.current;
          
                      if (runningActionRef.current) {
                          if (isMovingAfterLand) {
                              runningActionRef.current.reset().fadeIn(0.2).play(); // Fade in running
                              runningActionRef.current.setEffectiveWeight(1);
                          } else {
                              runningActionRef.current.fadeOut(0.2); // Fade out to idle pose
                              runningActionRef.current.setEffectiveWeight(0);
                          }
                      }
                  }
              }
          }
          
          // --- General Movement Animation Control ---
          const isCurrentlyMoving = keysPressed.current['ArrowUp'] || 
                                    keysPressed.current['ArrowDown'] || 
                                    keysPressed.current['ArrowLeft'] || 
                                    keysPressed.current['ArrowRight'] ||
                                    isTouchingMove.current;
          
          if (runningActionRef.current && !isJumping.current) {
              if (isCurrentlyMoving) {
                  if (!runningActionRef.current.isRunning()) {
                      runningActionRef.current.reset().fadeIn(0.2).play();
                  }
                  runningActionRef.current.setEffectiveWeight(1);
              } else {
                  if (runningActionRef.current.isRunning()) {
                      runningActionRef.current.fadeOut(0.2);
                  }
                  runningActionRef.current.setEffectiveWeight(0);
              }
          }


          // --- Camera Rotation Logic (A, W, S, D Keys & Touch) ---
          if (keysPressed.current['KeyA']) {
            cameraYaw.current += CAMERA_ROTATION_SPEED;
          }
          if (keysPressed.current['KeyD']) {
            cameraYaw.current -= CAMERA_ROTATION_SPEED;
          }
          if (keysPressed.current['KeyW']) {
            cameraPitch.current += CAMERA_ROTATION_SPEED;
            cameraPitch.current = Math.min(cameraPitch.current, Math.PI / 2 - 0.1);
          }
          if (keysPressed.current['KeyS']) {
            cameraPitch.current -= CAMERA_ROTATION_SPEED;
            cameraPitch.current = Math.max(cameraPitch.current, -Math.PI / 2 + 0.1);
          }

          // --- Character-Wall Collision Detection ---
          if (currentCharacter.position.x < MIN_X) currentCharacter.position.x = MIN_X;
          if (currentCharacter.position.x > MAX_X) currentCharacter.position.x = MAX_X;
          if (currentCharacter.position.z < MIN_Z) currentCharacter.position.z = MIN_Z;
          if (currentCharacter.position.z > MAX_Z) currentCharacter.position.z = MAX_Z;

          // --- Camera Follow and Orbit Logic ---
          const baseCameraOffset = new THREE.Vector3(0, CHARACTER_SIZE * 300, -CHARACTER_SIZE * 500);

          baseCameraOffset.applyQuaternion(currentCharacter.quaternion);

          const cameraQuaternion = new THREE.Quaternion().setFromEuler(
            new THREE.Euler(cameraPitch.current, cameraYaw.current, 0, 'YXZ')
          );

          const finalCameraOffset = baseCameraOffset.clone().applyQuaternion(cameraQuaternion);

          const idealCameraTargetPosition = currentCharacter.position.clone().add(finalCameraOffset);
          let cameraFinalPosition = idealCameraTargetPosition.clone();

          const raycaster = new THREE.Raycaster();
          const direction = new THREE.Vector3().subVectors(idealCameraTargetPosition, currentCharacter.position).normalize();
          raycaster.set(currentCharacter.position, direction);

          const intersects = raycaster.intersectObjects(wallsRef.current, false);

          if (intersects.length > 0) {
            const closestIntersection = intersects[0];
            const buffer = CHARACTER_SIZE * 25;
            if (closestIntersection.distance < currentCharacter.position.distanceTo(idealCameraTargetPosition)) {
              cameraFinalPosition.copy(closestIntersection.point).sub(direction.multiplyScalar(buffer));
            }
          }

          cameraFinalPosition.y = Math.max(cameraFinalPosition.y, currentCharacter.position.y + CHARACTER_SIZE * 10);
          currentCamera.position.copy(cameraFinalPosition);
          currentCamera.lookAt(currentCharacter.position);
        }

        renderer.render(scene, currentCamera);
      };

      animate();

    } catch (error) {
      console.error('Error initializing 3D scene:', error);
      setIsSceneInitialized(true);
    }

    // --- Cleanup Function ---
    return () => {
      clearTimeout(initTimeout); 
      if (mountRef.current && renderer && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      if (wallsRef.current.length > 0) {
        wallsRef.current.forEach(wall => {
          if (wall.geometry) wall.geometry.dispose();
          if (wall.material) {
            if (Array.isArray(wall.material)) {
              wall.material.forEach(mat => mat.dispose());
            } else {
              wall.material.dispose();
            }
          }
          scene.remove(wall);
        });
        wallsRef.current = [];
      }

      if (characterRef.current) {
        characterRef.current.traverse((object) => {
          if (object.isMesh) {
            if (object.geometry) object.geometry.dispose();
            if (object.material) {
              if (Array.isArray(object.material)) {
                object.material.forEach(mat => mat.dispose());
              } else {
                object.material.dispose();
              }
            }
          }
        });
        scene.remove(characterRef.current);
      }
      if (renderer) renderer.dispose();
      if (mixerRef.current) mixerRef.current.stopAllAction();

      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      mountRef.current?.removeEventListener('touchstart', handleTouchStart);
      mountRef.current?.removeEventListener('touchmove', handleTouchMove);
      mountRef.current?.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  // Handler for the on-screen jump button
  const handleJumpButtonClick = () => {
    if (characterRef.current && !isJumping.current) {
      isJumping.current = true;
      jumpVelocity.current = JUMP_INITIAL_VELOCITY;
      if (mixerRef.current && runningActionRef.current && jumpActionRef.current) {
        runningActionRef.current.stop();
        jumpActionRef.current.reset().play();
      } else {
        console.warn("Animation mixer or animation refs not ready for jump button. Character will only translate vertically.");
      }
    }
  };


  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#282c34',
      color: 'white',
      padding: '1rem',
      fontFamily: 'Inter, sans-serif'
    }}>
      <h1 style={{
        fontSize: '3rem',
        fontWeight: 'extrabold',
        marginBottom: '2rem',
        color: '#61dafb',
        textAlign: 'center'
      }}>
        3D Character Movement
      </h1>
      <p style={{
        fontSize: '1.125rem',
        marginBottom: '1.5rem',
        color: '#d1d5db',
        textAlign: 'center',
        maxWidth: '48rem'
      }}>
        Use **Arrow Keys** to move the human character. Use **W, A, S, D** to rotate the camera. The character will now "bump" into the room walls and should play a running animation when moving. Press **Spacebar** to jump.
        <br />
        For **mobile**, touch and drag on the **left half** of the screen to move, and on the **right half** to rotate the camera. Use the **Jump button** for jumping.        
      </p>
      <div
        ref={mountRef}
        style={{
          width: '100%',
          maxWidth: '64rem',
          height: '24rem', // Fixed height for the 3D canvas itself
          backgroundColor: '#4a4a4a',
          borderRadius: '0.75rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          border: '2px solid #3b82f6',
          touchAction: 'none' // Prevents default touch behaviors like scrolling/zooming
        }}
      >
        {!isSceneInitialized && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            fontSize: '1.25rem',
            color: '#9ca3af'
          }}>
            Loading 3D environment and character model...
          </div>
        )}
      </div>
      {/* On-screen Jump Button for Mobile */}
      <button
        onClick={handleJumpButtonClick}
        style={{
          marginTop: '1.5rem',
          padding: '0.75rem 2rem',
          fontSize: '1.2rem',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: '#3b82f6',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          transition: 'background-color 0.3s ease, transform 0.1s ease',
          zIndex: 10, // Ensure button is above canvas for touch events
        }}
        onMouseDown={(e) => e.preventDefault()} // Prevent losing focus on canvas
        onTouchStart={(e) => e.preventDefault()} // Prevent touch-related issues
      >
        Jump
      </button>
      <p style={{
        marginTop: '2rem',
        fontSize: '1rem',
        color: '#9ca3af',
        textAlign: 'center',
        maxWidth: '48rem'
      }}>
        This simulation loads an FBX character model, plays its animation based on movement and jump, and features collision detection within a defined room.
      </p>
    </div>
  );
}

export default App;
